import { IMemberChangeAgreePayload } from '@/models/Member/interfaces/payloads/IMemberChangeAgreePayload';
import MemberActions from '@/models/Member/MemberActions';
import MemberRequestBuilder from '@/models/Member/MemberRequestBuilder';
import { PolicyActions } from '@/models/Policy/PolicyActions';
import { PolicyClientRequestBuilder } from '@/models/Policy/PolicyRequestBuilder';
import { YaMo } from '@/services/YaMoment';
import { ToastStore } from '@/stores/partial/ToastStore';
import { nullish, pickRight } from '@/utils/yambda';
import { getYaNative } from '@yanolja-org/ya-native';
import { action, computed, observable } from 'mobx';
import { prop } from 'ramda';

export interface IMarketingTemrsStoreProps {
  marketingPrivacy: boolean;
  marketingPush: boolean;
  toastStore: ToastStore;
  deviceOS: 'PW' | 'MW';
  // 프로모션 페이지에서 사용되는 로그 스테이터스 값
  specialPromotionName?: string;
  promotionStatus?: string;
}

export class MarketingTermsStore {
  @observable isLoading = false;
  @observable isOpen = false;
  @observable isTermModalOpen = false;
  @observable marketingPrivacyTerm: string | undefined;
  @observable isAgreeMarketingPrivacy: boolean;
  @observable marketingPrivacy: boolean;
  @observable marketingPush: boolean;
  @observable specialPromotionName?: string;
  @observable promotionStatus?: string;
  @observable callback?: (result?: IMemberChangeAgreePayload) => void;

  private readonly toastStore: ToastStore;
  private readonly action = new MemberActions(MemberRequestBuilder);
  private readonly policyActions = new PolicyActions(PolicyClientRequestBuilder);
  private readonly deviceOS: 'PW' | 'MW';

  constructor({
    marketingPrivacy,
    marketingPush,
    toastStore,
    deviceOS,
    specialPromotionName,
    promotionStatus,
  }: IMarketingTemrsStoreProps) {
    this.marketingPrivacy = marketingPrivacy;
    this.marketingPush = marketingPush;
    this.isAgreeMarketingPrivacy = marketingPrivacy;
    this.toastStore = toastStore;
    this.deviceOS = deviceOS;
    this.specialPromotionName = specialPromotionName;
    this.promotionStatus = promotionStatus;
  }

  @computed
  get Submittable(): boolean {
    return this.isAgreeMarketingPrivacy;
  }

  @computed
  get IsAgreedMarketing(): boolean {
    return this.marketingPrivacy && this.marketingPush;
  }

  @action
  async openTermModal(): Promise<void> {
    this.isTermModalOpen = true;

    if (!this.marketingPrivacyTerm) {
      this.marketingPrivacyTerm = pickRight(
        (
          await this.policyActions.find({
            query: {
              t: 'privacy_short_member_optional',
              a: 'get',
            },
          })
        ).map(prop('data')),
      );
    }
  }

  @action.bound
  close(): void {
    this.isOpen = false;
  }

  @action.bound
  closeTerm(): void {
    this.isTermModalOpen = false;
  }

  @action.bound
  toggleMarketingPrivacyTerm(): void {
    this.isAgreeMarketingPrivacy = !this.isAgreeMarketingPrivacy;
  }

  @action.bound
  async agree(): Promise<void> {
    if (this.isLoading || !this.Submittable) {
      return;
    }

    this.isLoading = true;

    const result = await this.action.changeAgree({
      body: {
        device_os: this.deviceOS,
        agree: this.marketingPrivacy
          ? {
              marketing_push: true,
            }
          : {
              marketing_push: true,
              marketing_privacy: true,
            },
      },
    });

    const data = result.caseOf({
      left: nullish,
      right: prop('data'),
    });

    this.isLoading = false;

    if (data) {
      this.marketingPrivacy = true;
      this.marketingPush = true;

      this.isOpen = false;
      this.toastStore.addItem({
        key: 'marketing-terms-agree',
        text: `[NOL] ${YaMo.getSeoulTime().format('YYYY-MM-DD')} 이벤트·혜택 Push 알림 받기 동의 처리되었습니다.`,
        timeout: 2000,
      });

      const yaNative = getYaNative();
      if (yaNative.isEnabled) {
        yaNative.agreementUpdate(undefined, false);
      }

      if (this.callback) {
        this.callback(data);
      }
    }
  }
}
